/**
 * «скачать ▾» — четыре файла заказа фабрике (спека …-plans-export-smart, часть 2):
 * «План» / «Размер», РФ / Китай. Файл строит своя ручка `/api/plans/export`,
 * поэтому пункт — простая `<a href download>`: тот же источник, кука сессии едет
 * сама, имя файла ставит `Content-Disposition` сервера.
 *
 * ПАНЕЛЬ — ПОРТАЛОМ В `body` С `position: fixed`. Меню стоит и в полосе выбора,
 * и в строке группы таблицы, а строка группы живёт внутри виртуального
 * scroll-контейнера: `absolute` панель там обрезалась бы его краем и уходила бы
 * под соседние строки (у каждой свой `transform`, то есть свой слой). Места
 * вниз не хватает — панель встаёт над кнопкой. Прокрутка и смена размера окна
 * закрывают её: прилипать к уехавшей кнопке панели незачем.
 *
 * Клики и клавиши внутри гасятся на корне (Esc перед этим закрывает меню): события портала React поднимает по
 * дереву компонентов, и без этого пункт меню свернул бы группу под ним.
 */

import { ChevronDown, Download } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { exportHref } from "@/lib/plans";
import { usePopover } from "@/lib/usePopover";

const ФАЙЛЫ = [
  { kind: "list", lang: "ru", label: "План РФ" },
  { kind: "list", lang: "cn", label: "План CN" },
  { kind: "size", lang: "ru", label: "Размер РФ" },
  { kind: "size", lang: "cn", label: "Размер CN" },
] as const;

const ШИРИНА = 150;
/** Четыре строки по 28 px и поля панели — хватает, чтобы решить «вниз или вверх». */
const ВЫСОТА = 4 * 28 + 10;

export function PlanDownloadMenu({
  ids,
  qr,
  disabled,
  compact = false,
}: {
  ids?: number[] | undefined;
  qr?: number | undefined;
  /** Почему скачать нельзя, словами; пусто — можно. */
  disabled?: string | undefined;
  /** Значок без подписи — для строки группы, где места нет. */
  compact?: boolean;
}) {
  const [место, setМесто] = useState<{ left: number; top: number } | null>(null);
  const кнопка = useRef<HTMLButtonElement>(null);
  const панель = useRef<HTMLDivElement>(null);
  const open = место !== null;

  const закрыть = () => setМесто(null);

  useLayoutEffect(() => {
    if (!open || !кнопка.current) return;
    const b = кнопка.current.getBoundingClientRect();
    const left = Math.max(8, Math.min(b.left, window.innerWidth - ШИРИНА - 8));
    const вниз = b.bottom + 4 + ВЫСОТА <= window.innerHeight - 8;
    const top = вниз ? b.bottom + 4 : Math.max(8, b.top - 4 - ВЫСОТА);
    if (left !== место.left || top !== место.top) setМесто({ left, top });
  }, [open, место]);

  // Панель рисуется порталом: «внутри» — и кнопка, и сама панель.
  usePopover(open, закрыть, [кнопка, панель]);

  useEffect(() => {
    if (!open) return;
    // `capture`: прокручивается не окно, а контейнер таблицы.
    window.addEventListener("scroll", закрыть, true);
    window.addEventListener("resize", закрыть);
    return () => {
      window.removeEventListener("scroll", закрыть, true);
      window.removeEventListener("resize", закрыть);
    };
  }, [open]);

  const подсказка =
    disabled || (qr ? `файлы заказа фабрике по всей QR-папке ${qr}` : "файлы заказа фабрике по отмеченным партиям");

  return (
    <span
      className="relative inline-flex flex-none"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        // Esc — здесь, а не только слушателем документа: гашение ниже не
        // пустило бы событие до `document`, и меню с фокусом внутри не
        // закрывалось бы.
        if (e.key === "Escape") закрыть();
        e.stopPropagation();
      }}
      data-annot={qr ? `скачать QR ${qr}` : "скачать отмеченные"}
    >
      <button
        ref={кнопка}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={Boolean(disabled)}
        title={подсказка}
        aria-label={compact ? подсказка : undefined}
        onClick={() => (open ? закрыть() : setМесто({ left: -9999, top: -9999 }))}
        className={
          compact
            ? "flex h-[21px] items-center rounded border border-border-strong px-1 text-fg-muted hover:border-accent hover:text-accent-text"
            : "flex items-center gap-0.5 font-medium text-accent-text hover:underline disabled:cursor-not-allowed disabled:text-fg-faint disabled:no-underline"
        }
      >
        {compact ? (
          <Download className="h-3.5 w-3.5" />
        ) : (
          <>
            скачать <ChevronDown className="h-3.5 w-3.5" />
          </>
        )}
      </button>
      {open
        ? createPortal(
            <div
              ref={панель}
              role="menu"
              style={{ left: место.left, top: место.top, width: ШИРИНА }}
              className="fixed z-50 rounded-[var(--radius-ui)] border border-border bg-bg-elevated py-1 text-[12.5px] shadow-lg"
              data-annot="меню скачать"
            >
              {ФАЙЛЫ.map((f) => (
                <a
                  key={f.label}
                  role="menuitem"
                  href={exportHref(f.kind, f.lang, { ids, qr })}
                  download
                  // Закрыть ПОСЛЕ клика, а не в нём: ссылку, снятую с
                  // документа до конца обработки, браузер не переходит — и
                  // файл не скачался бы.
                  onClick={() => setTimeout(закрыть, 0)}
                  className="flex h-7 items-center px-3 hover:bg-bg-hover focus-visible:bg-bg-hover focus-visible:outline-none"
                >
                  {f.label}
                </a>
              ))}
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}
